import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowRight, Bed, Bath, Maximize2, MapPin, Sparkles } from "lucide-react";
import axios from "axios";
import "./Offer.css";

const FALLBACK_OFFERS = [
  {
    id: "off-1",
    title: "The Grand Crest Villa & Private Garden",
    price: 32500000,
    area: { name: "Indiranagar", city: { name: "Bangalore" } },
    type: { name: "Luxury Villa" },
    discountBadge: "Special Festival Offer",
    structure: { bed_rooms: 4, bath_rooms: 5, built_up_area: "4,200" },
    image_url: "https://images.unsplash.com/photo-1613490493576-7fde63acd811?q=80&w=1000&auto=format&fit=crop",
  },
  {
    id: "off-2",
    title: "Sea-Facing Ultra Modern Penthouse",
    price: 48000000,
    area: { name: "Worli Sea Face", city: { name: "Mumbai" } },
    type: { name: "Penthouse" },
    discountBadge: "Zero Brokerage",
    structure: { bed_rooms: 3, bath_rooms: 4, built_up_area: "3,100" },
    image_url: "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?q=80&w=1000&auto=format&fit=crop",
  },
  {
    id: "off-3",
    title: "High-Tech Smart Living Duplex Home",
    price: 18500000,
    area: { name: "Gachibowli", city: { name: "Hyderabad" } },
    type: { name: "Duplex" },
    discountBadge: "Ready to Move",
    structure: { bed_rooms: 3, bath_rooms: 3, built_up_area: "2,400" },
    image_url: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?q=80&w=1000&auto=format&fit=crop",
  },
  {
    id: "off-4",
    title: "Golf View Luxury Residence & Clubhouse",
    price: 26000000,
    area: { name: "Golf Course Road", city: { name: "Gurugram" } },
    type: { name: "Apartment" },
    discountBadge: "Exclusive Pre-Launch",
    structure: { bed_rooms: 4, bath_rooms: 4, built_up_area: "3,650" },
    image_url: "https://images.unsplash.com/photo-1600585154526-990dced4db0d?q=80&w=1000&auto=format&fit=crop",
  },
];

const formatINR = (val) => {
  if (!val) return "Price on Request";
  const n = Number(val);
  if (n >= 10000000) return `₹ ${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹ ${(n / 100000).toFixed(2)} Lac`;
  return `₹ ${n.toLocaleString("en-IN")}`;
};

const Offer = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);
  const baseUrl = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${baseUrl}/all/property/latestList`);
        if (
          response.data?.statusType === "SUCCESS" &&
          response.data.properties &&
          response.data.properties.length > 0
        ) {
          setOffers(response.data.properties);
        } else {
          setOffers(FALLBACK_OFFERS);
        }
      } catch (err) {
        setOffers(FALLBACK_OFFERS);
      } finally {
        setLoading(false);
      }
    };
    fetchOffers();
  }, [baseUrl]);

  const handleScroll = (direction) => {
    if (scrollRef.current) {
      const { scrollLeft, clientWidth } = scrollRef.current;
      const scrollAmount = clientWidth * 0.8;
      scrollRef.current.scrollTo({
        left: direction === "left" ? scrollLeft - scrollAmount : scrollLeft + scrollAmount,
        behavior: "smooth",
      });
    }
  };

  return (
    <section className="offers">
      <div className="offers__container">
        <div className="offers__header">
          <div>
            <span className="offers__label">Exclusive Opportunities</span>
            <h2 className="offers__title">
              Top Featured Deals & <em>Exclusive Offers</em>
            </h2>
          </div>
          <div className="offers__nav-btns">
            <button
              type="button"
              className="offers__nav-btn"
              onClick={() => handleScroll("left")}
              aria-label="Previous properties"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              className="offers__nav-btn"
              onClick={() => handleScroll("right")}
              aria-label="Next properties"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        <div className="offers__carousel" ref={scrollRef}>
          {offers.map((item, idx) => {
            const imgSrc =
              item.images && item.images.length > 0
                ? item.images[0].image_url?.startsWith("http")
                  ? item.images[0].image_url
                  : `${process.env.REACT_APP_ASSET_URL || "https://images.allhomedesire.in"}/${item.images[0].image_url}`
                : item.image_url || FALLBACK_OFFERS[idx % FALLBACK_OFFERS.length].image_url;

            const beds = item.structure?.bed_rooms || 3;
            const baths = item.structure?.bath_rooms || 3;
            const areaVal = item.structure?.built_up_area || "2,200";

            return (
              <div key={item.id || idx} className="offers__card">
                <div className="offers__card-media">
                  <img
                    src={imgSrc}
                    alt={item.title || item.name || "AHD Featured Property"}
                    className="offers__card-img"
                  />
                  <span className="offers__card-badge">
                    <Sparkles size={11} /> {item.discountBadge || "Hot Deal"}
                  </span>
                </div>

                <div className="offers__card-body">
                  <div className="offers__card-price-row">
                    <span className="offers__card-price">
                      {formatINR(item.price || item.budget)}
                    </span>
                    <span className="offers__card-tag">
                      {item.type?.name || "Premium Villa"}
                    </span>
                  </div>

                  <h3 className="offers__card-title">
                    {item.title || item.name || "Exclusive Luxury Estate"}
                  </h3>

                  <p className="offers__card-location">
                    <MapPin size={13} />
                    <span>
                      {item.area?.name || "Prime Sector"},{" "}
                      {item.area?.city?.name || item.city?.name || "India"}
                    </span>
                  </p>

                  <div className="offers__card-specs">
                    <div className="offers__spec">
                      <Bed size={13} />
                      <span>{beds} BHK</span>
                    </div>
                    <div className="offers__spec">
                      <Bath size={13} />
                      <span>{baths} Baths</span>
                    </div>
                    <div className="offers__spec">
                      <Maximize2 size={12} />
                      <span>{areaVal} sq.ft</span>
                    </div>
                  </div>

                  <Link to="/property-list" className="offers__card-btn">
                    <span>View Deal Details</span>
                    <ArrowRight size={14} strokeWidth={2.5} />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Offer;
